import { Skeleton, Stack, TextField, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import React, { useState } from "react"
import { StandardDialog } from "../../../../../shared/components/dialogs/StandardDialog"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { fetchUserLocations, GetCanteenLocationsResponse, saveUserKitchenLocation, UserWeekMenu } from "../weekMenuApi"
import { faLocationDot } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { LocationKitchen, UserLocation } from "../weekMenuTypes"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { StandardLoadingButton } from "../../../../../shared/components/buttons/LoadingButton"

const useStyles = makeStyles()(() => ({
    content: {
        padding: "0 2.5rem 2.5rem 2.5rem ",
    },
    dialog: {
        backgroundColor: "#F1F5F9",
    },
    kitchenItem: {
        border: "2px solid transparent",
        padding: "1rem",
        borderRadius: ".75rem",
        backgroundColor: "white",
    },
    kitchenItemSelected: {
        border: "2px solid #2196F3",
    },
    kitchenImg: {
        maxWidth: "9rem",
        height: "auto",
        objectFit: "contain",
    },
    filterInput: {
        backgroundColor: "white",
    },
    skeletonItem: {
        borderRadius: ".75rem",
    },
}))

const LoadingSkeletons = () => {
    const { classes } = useStyles()

    return (
        <>
            {Array.from({ length: 3 }).map((_, index) => (
                <Stack direction="column" spacing="1rem" key={index}>
                    <Stack direction="row" alignItems="center" spacing="1rem">
                        <Skeleton variant="circular" width={20} height={20} />
                        <Skeleton variant="text" width={150} height={32} />
                    </Stack>
                    <Skeleton variant="rectangular" height={180} className={classes.skeletonItem} />
                </Stack>
            ))}
        </>
    )
}

type SelectCanteenLocationDialogProps = Readonly<{
    selectedLocationId?: number
    open: boolean
    onSelect: (canteen: LocationKitchen) => void
    onClose: () => void
}>

const transformLocations = (data: GetCanteenLocationsResponse) => {
    return data
}

export function SelectCanteenLocationDialog({
    open,
    onClose,
    selectedLocationId,
    onSelect,
}: SelectCanteenLocationDialogProps) {
    const { classes, cx } = useStyles()
    const [filter, setFilter] = useState("")

    const { data: locations, isLoading } = useApiQuery(fetchUserLocations, {
        queryName: UserWeekMenu.ORGANIZATION_LOCATIONS_CANTEENS,
        dependencies: {},
        select: transformLocations,
    })

    const { mutate, isLoading: isSaving } = useApiMutation(saveUserKitchenLocation, {
        async onSuccess() {
            onClose()
        },
    })

    const filteredLocations = locations?.filter((location) =>
        location.name.toLowerCase().includes(filter.toLowerCase()),
    )

    const onLocationClick = (locationId: UserLocation, kitchen: LocationKitchen) => {
        mutate({ locationId: locationId.id, kitchenId: kitchen.id })
        onSelect(kitchen)
    }

    return (
        <StandardDialog
            slotProps={{
                paper: {
                    className: classes.dialog,
                },
            }}
            scroll="body"
            open={open}
            onClose={onClose}
            dialogTitle={
                selectedLocationId === undefined ? (
                    <LocalizedStrict id="user-home-week-menu-select-location-dialog-title-first-time">
                        <>Choose location</>
                    </LocalizedStrict>
                ) : (
                    <LocalizedStrict id="user-home-week-menu-select-location-dialog-title">
                        <>Change location</>
                    </LocalizedStrict>
                )
            }
        >
            <Stack direction="column" spacing="2.5rem" className={classes.content}>
                <LocalizedStrict id="user-home-week-menu-select-location-search-filter" attrs={{ placeholder: true }}>
                    <TextField
                        className={classes.filterInput}
                        fullWidth
                        variant="outlined"
                        placeholder="Search on name, address, shop number..."
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                    />
                </LocalizedStrict>
                {isLoading ? (
                    <LoadingSkeletons />
                ) : (
                    filteredLocations?.map((location) => (
                        <Stack direction="column" spacing="1rem" key={location.id}>
                            <Stack direction="row" alignItems="center" spacing="1rem">
                                <FontAwesomeIcon color="black" icon={faLocationDot} />
                                <Typography variant="h6">{location.name}</Typography>
                            </Stack>
                            <Stack direction="column" spacing="1rem">
                                {location?.kitchens?.map((kitchen) => (
                                    <Stack
                                        direction="column"
                                        spacing="1rem"
                                        key={kitchen.id}
                                        className={cx(
                                            classes.kitchenItem,
                                            selectedLocationId === kitchen.id ? classes.kitchenItemSelected : undefined,
                                        )}
                                    >
                                        <Stack direction="row" justifyContent="space-between">
                                            <Stack direction="column" spacing=".5rem">
                                                <Typography variant="subtitle1">{kitchen?.name}</Typography>
                                                <Stack direction="column">
                                                    <Typography variant="subtitle2">
                                                        {kitchen?.sellerSupplierParty?.name}
                                                    </Typography>
                                                    <Typography variant="body2">{kitchen?.address?.city}</Typography>
                                                    <Typography variant="body2">
                                                        {kitchen?.address?.streetNumber} {kitchen?.address?.streetName}
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                            {kitchen?.brandingDetails?.imageUrl && (
                                                <img
                                                    alt="location image"
                                                    src={kitchen?.brandingDetails?.imageUrl}
                                                    className={classes.kitchenImg}
                                                />
                                            )}
                                        </Stack>
                                        <LocalizedStrict id="user-home-week-menu-select-location-dialog-kitchen-item-button">
                                            <StandardLoadingButton
                                                loading={isSaving}
                                                disabled={selectedLocationId === kitchen.id}
                                                onClick={() => onLocationClick(location, kitchen)}
                                            >
                                                Select
                                            </StandardLoadingButton>
                                        </LocalizedStrict>
                                    </Stack>
                                ))}
                            </Stack>
                        </Stack>
                    ))
                )}
            </Stack>
        </StandardDialog>
    )
}
